import DOMPurify from "dompurify";

export const sanitizeString = (value: string): string => {
  if (!value) return "";
  // Remove any HTML tags and sanitize the string
  const sanitized = DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [], // Don't allow any HTML tags
    ALLOWED_ATTR: [], // Don't allow any attributes
  });
  // Escape special characters
  return sanitized
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

/**
 * `String.trim()` only strips characters with the Unicode `White_Space` property, so a
 * zero-width space, word joiner, BOM, Hangul filler or stray control character survives
 * it and reads as a filled-in name while rendering as nothing. Drop the invisibles, fold
 * every space-like run to one plain space, then trim — an empty result means blank.
 *
 * Blocklisting alone cannot close this: the Hangul fillers (U+115F, U+1160, U+3164,
 * U+FFA0) are letters (`\p{Lo}`) and the Braille blank is a symbol (`\p{So}`), so each
 * has to be listed by hand, and a lone combining mark stays invisible under any property
 * class worth naming. Hence the closing rule — a name must carry at least one letter or
 * digit. That is a gate rather than another filter on purpose: stripping `\p{Mn}`
 * outright would mangle decomposed accents ("Jose\u0301") and most Indic and Arabic names.
 */
const INVISIBLE_CHARS = /[\p{Cf}\u115F\u1160\u3164\uFFA0]/gu;

// Control characters part words as often as they pad them — a pasted "John\tDoe" is two
// names, not one — so they fold to a space alongside the Unicode spaces rather than being
// dropped. Line and paragraph separators come along too \u2014 an interior U+2028 renders as a
// line break. U+2800 joins them: the Braille blank is that script's space.
const SPACE_LIKE = /[\p{Cc}\p{Zs}\p{Zl}\p{Zp}\u2800]+/gu;

const VISIBLE_CHAR = /[\p{L}\p{N}]/u;

export const normalizeName = (value: string): string => {
  const normalized = value.replace(INVISIBLE_CHARS, "").replace(SPACE_LIKE, " ").trim();
  return VISIBLE_CHAR.test(normalized) ? normalized : "";
};

export const sanitizeNumber = (value: string): string => {
  // Only allow digits and one decimal point
  return value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
};

export const sanitizeFilename = (filename: string): string => {
  if (!filename) return "download";

  // Remove any path traversal attempts and normalize the filename
  const sanitized = filename.replace(/^.*[\\\/]/, "");

  // Replace problematic characters but preserve most of the original filename structure
  // Keep alphanumeric, dots, hyphens, underscores, spaces, and parentheses
  // eslint-disable-next-line no-control-regex -- Control chars need to be removed from filenames
  const cleaned = sanitized.replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");

  // Ensure the filename isn't empty after cleaning
  return cleaned.trim() || "download";
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Strip internal chatbot <<CHOICES>>/<<QUESTION>> tags that may leak into UI.
 * Defense-in-depth: backend should strip these, but this is a frontend safety net.
 */
const CHOICE_TAG_RE = /<<\/?(?:CHOICES?|QUESTION)>>/g;

export const stripChoiceTags = (text: string): string => {
  if (!text) return "";
  return text.replace(CHOICE_TAG_RE, "").trim();
};

export const sanitizeHtml = (value: string, allowedTags: string[] = []): string => {
  if (!value) return "";
  // More restrictive HTML sanitization for specific cases where some HTML is needed
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: ["href", "target", "rel", "title", "alt", "src"],
  });
};
